import isURL from 'validator/lib/isURL';
import { CTA_DEFAULT_COLOR } from './editorConfig';
import {
  ERROR_INVALID_URL,
  ERROR_REQUIRED,
} from '../../../../../../shared/constants/validationErrors';
import {
  setupDialogHasValue,
  showFieldError,
  setDialogSubmitEnabled,
  watchDialogInputs,
} from './dialogUtils';

function buildCtaStyle(color) {
  return [
    'display: inline-block',
    'padding: 12px 24px',
    `background-color: ${color}`,
    'color: #ffffff',
    'text-decoration: none',
    'border-radius: 4px',
    'font-weight: bold',
  ].join('; ');
}

const linkTag = (href, encodedText) => `<a href="${href}">${encodedText}</a>`;

const ctaTag = (href, color, encodedText) =>
  `<a href="${href}" data-cta="true" style="${buildCtaStyle(color)}">${encodedText}</a>`;

function normalizeUrl(url) {
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) return `mailto:${url}`;

  const withProtocol = url.includes('://') ? url : `https://${url}`;
  return isURL(withProtocol, { require_tld: true }) ? withProtocol : null;
}

function dialogButtons(isEdit) {
  return [
    { type: 'cancel', text: 'Cancel' },
    { type: 'submit', text: isEdit ? 'Save' : 'Insert', primary: true },
  ];
}

function setupLinkButton(editor, isPlainText) {
  editor.ui.registry.addButton('customlink', {
    icon: 'link',
    tooltip: 'Insert Link',
    onAction: () => {
      const foundAnchor = editor.dom.getParent(editor.selection.getNode(), 'a');
      const linkNode = foundAnchor?.hasAttribute('data-cta') ? null : foundAnchor;
      const existingUrl = linkNode?.getAttribute('href') || '';
      const existingText =
        linkNode?.textContent || editor.selection.getContent({ format: 'text' }) || '';

      const bodyItems = isPlainText
        ? [{ type: 'input', name: 'url', label: 'URL' }]
        : [
            { type: 'input', name: 'text', label: 'Text' },
            { type: 'input', name: 'url', label: 'URL' },
          ];

      const initialData = isPlainText
        ? { url: existingUrl }
        : { text: existingText, url: existingUrl };

      editor.windowManager.open({
        title: linkNode ? 'Edit Link' : 'Insert Link',
        body: { type: 'panel', items: bodyItems },
        initialData,
        buttons: dialogButtons(!!linkNode),
        onSubmit: (api) => {
          const { text, url } = api.getData();
          if (!url?.trim()) {
            showFieldError('URL', ERROR_REQUIRED);
            setDialogSubmitEnabled(false);
            return;
          }
          const normalizedUrl = normalizeUrl(url.trim());
          if (!normalizedUrl) {
            showFieldError('URL', ERROR_INVALID_URL);
            setDialogSubmitEnabled(false);
            return;
          }
          if (isPlainText) {
            if (linkNode) {
              editor.dom.setAttrib(linkNode, 'href', normalizedUrl);
              linkNode.textContent = normalizedUrl;
            } else {
              editor.insertContent(linkTag(normalizedUrl, editor.dom.encode(normalizedUrl)));
            }
          } else {
            const displayText = text.trim() || normalizedUrl;
            if (linkNode) {
              editor.dom.setAttrib(linkNode, 'href', normalizedUrl);
              linkNode.textContent = text.trim() || normalizedUrl;
            } else {
              editor.insertContent(linkTag(normalizedUrl, editor.dom.encode(displayText)));
            }
          }
          api.close();
        },
      });

      watchDialogInputs();
    },
  });
}

function setupCtaButton(editor) {
  editor.ui.registry.addButton('ctabutton', {
    text: 'Button',
    tooltip: 'Insert CTA Button',
    onAction: () => {
      const ctaNode = editor.dom.getParent(editor.selection.getNode(), 'a[data-cta]');
      const existingText = ctaNode?.textContent?.trim() || '';
      const existingUrl = ctaNode?.getAttribute('href') || '';
      const existingColor = ctaNode?.style?.backgroundColor
        ? rgbToHex(ctaNode.style.backgroundColor)
        : CTA_DEFAULT_COLOR;

      editor.windowManager.open({
        title: ctaNode ? 'Edit Button' : 'Insert Button',
        body: {
          type: 'panel',
          items: [
            { type: 'input', name: 'text', label: 'Button text' },
            { type: 'input', name: 'url', label: 'URL' },
            { type: 'colorinput', name: 'color', label: 'Color' },
          ],
        },
        initialData: {
          text: existingText,
          url: existingUrl,
          color: existingColor,
        },
        buttons: dialogButtons(!!ctaNode),
        onSubmit: (api) => {
          const { text, url, color } = api.getData();
          const textError = !text?.trim() ? ERROR_REQUIRED : null;
          const urlError = !url?.trim() ? ERROR_REQUIRED : null;
          if (textError) showFieldError('Button text', textError);
          if (urlError) showFieldError('URL', urlError);
          if (textError || urlError) {
            setDialogSubmitEnabled(false);
            return;
          }
          const normalizedUrl = normalizeUrl(url.trim());
          if (!normalizedUrl) {
            showFieldError('URL', ERROR_INVALID_URL);
            setDialogSubmitEnabled(false);
            return;
          }
          const html = ctaTag(normalizedUrl, color, editor.dom.encode(text.trim()));
          if (ctaNode) {
            editor.dom.setOuterHTML(ctaNode, html);
          } else {
            editor.insertContent(html);
          }
          api.close();
        },
      });

      watchDialogInputs();
    },
  });
}

function rgbToHex(rgb) {
  const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (!match) return CTA_DEFAULT_COLOR;
  return (
    '#' +
    match
      .slice(1)
      .map((n) => parseInt(n).toString(16).padStart(2, '0'))
      .join('')
  );
}

export function setupEditor(isPlainText) {
  return (editor) => {
    setupDialogHasValue(editor);
    setupLinkButton(editor, isPlainText);
    setupCtaButton(editor);
  };
}
